import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { ObservationRecord } from '@models/observation';

import type { ListLatestObservationsQueryVariables } from 'generated/observation.generated';
import {
  useListLatestObservationsQuery,
  useListLatestObservationsLazyQuery,
} from 'generated/observation.generated';
import type { GetLatestObservationsModel } from 'generated/types';

// export type ObservationCategory =
//   | 'activity'
//   | 'exam'
//   | 'imaging'
//   | 'laboratory'
//   | 'procedure'
//   | 'social-history'
//   | 'survey'
//   | 'therapy'
//   | 'vital-signs';

const mapObservations = (data?: Partial<GetLatestObservationsModel>) => {
  if (!data) {
    return {};
  }
  const observationObj: Record<string, ObservationRecord | null> = {};
  Object.entries(data).map(([key, value]) => {
    if (typeof value !== 'string') {
      observationObj[key] = value ? new ObservationRecord(value) : null;
    }
  });
  return observationObj;
};

export const useGetLatestObservations = (
  params?: Omit<ListLatestObservationsQueryVariables, 'input'>
) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useListLatestObservationsQuery({
    variables: {
      input: {
        patient: patient_id,
      },
      ...params,
    },
    skip: !patient_id || !params,
  });
  handleError(response.error);
  return {
    ...response,
    data: mapObservations({
      ...response.data?.listLatestBodyObservations,
      ...response.data?.listLatestFitnessObservations,
      ...response.data?.listLatestSleepObservations,
      ...response.data?.listLatestVitalSignObservations,
    }),
  };
};

export const useLazyGetLatestObservations = () => {
  const { handleError } = useResponseHandler();
  const [query, response] = useListLatestObservationsLazyQuery();

  async function fetch(
    patient?: string,
    params?: Omit<ListLatestObservationsQueryVariables, 'input'>
  ) {
    const response = await query({
      variables: {
        input: {
          patient: patient ?? '',
        },
        ...params,
      },
    });
    handleError(response.error);
    return {
      ...response,
      data: mapObservations({
        ...response.data?.listLatestBodyObservations,
        ...response.data?.listLatestFitnessObservations,
        ...response.data?.listLatestSleepObservations,
        ...response.data?.listLatestVitalSignObservations,
      }),
    };
  }

  return {
    query: fetch,
    response: {
      ...response,
      data: mapObservations({
        ...response.data?.listLatestBodyObservations,
        ...response.data?.listLatestFitnessObservations,
        ...response.data?.listLatestSleepObservations,
        ...response.data?.listLatestVitalSignObservations,
      }),
    },
  };
};
